/**
 * DevDash Gating Logic
 *
 * Controls when DevDash is available:
 *   1. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   2. ON if localStorage devdash.enabled=true (user opted in via URL)
 *   3. OFF otherwise (opt-in required)
 *
 * Users must visit /?devdash=true to enable DevDash.
 * On first activation a browser prompt asks for a password, validated
 * client-side against the NEXT_PUBLIC_DEVDASH_PASSWORD env var.
 * If the env var is not set (e.g. local dev), any non-empty input is accepted.
 *
 * DevDash is available in ALL environments (including production) for employees.
 * Regular users are not affected since DevDash requires explicit opt-in.
 */

const STORAGE_KEY_ENABLED = 'devdash.enabled';
const URL_PARAM_ENABLE = 'devdash';

/**
 * Get the current environment
 */
// biome-ignore lint/nursery/useExportsLast: Function exported at definition for code organization
export function getCurrentEnvironment(): string {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.ENVIRONMENT?.toLowerCase() || 'local';
}

/**
 * Check if DevDash is disabled via environment variable
 */
function isDisabledByEnvVar(): boolean {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.NEXT_PUBLIC_DEVDASH_DISABLED === 'true';
}

/**
 * Check if the user has enabled DevDash via localStorage (opt-in)
 */
export function isDevDashEnabledByUser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(STORAGE_KEY_ENABLED) === 'true';
  } catch {
    return false;
  }
}

/**
 * Enable DevDash by setting localStorage flag
 */
export function enableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY_ENABLED, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Disable DevDash by removing localStorage flag (logout)
 */
export function disableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY_ENABLED);
  } catch {
    // Ignore storage errors
  }
}

/** Result of the URL-based DevDash activation check */
export type ActivationResult = 'activated' | 'denied' | 'deactivated' | 'none';

/**
 * Clean the devdash parameter from the current URL
 */
function cleanDevDashParam(): void {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(URL_PARAM_ENABLE);
  const newSearch = urlParams.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);
}

/**
 * Validate a password entered by the user against the client-side env var.
 * If NEXT_PUBLIC_DEVDASH_PASSWORD is not set (local dev), any non-empty input is accepted.
 */
function validatePassword(input: string): boolean {
  if (!input) {
    return false;
  }
  // biome-ignore lint/style/noProcessEnv: Client-side password for DevDash access gating
  const expected = process.env.NEXT_PUBLIC_DEVDASH_PASSWORD;

  // If no password is configured (local dev), accept any non-empty input
  if (!expected) {
    return true;
  }

  return input === expected;
}

/**
 * Check URL for DevDash enable parameter and prompt for password if found.
 * Call this on app initialization to handle the activation URL.
 *
 * Returns:
 *   'activated'   - password was correct, DevDash is now enabled
 *   'denied'      - password was wrong or prompt was cancelled
 *   'deactivated' - ?devdash=false was used to logout
 *   'none'        - no devdash param in URL
 */
export function checkUrlForDevDashEnable(): ActivationResult {
  if (typeof window === 'undefined') {
    return 'none';
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const devdashParam = urlParams.get(URL_PARAM_ENABLE);

    // No param — nothing to do
    if (devdashParam === null) {
      return 'none';
    }

    // ?devdash=false — logout (no password needed)
    if (devdashParam === 'false') {
      disableDevDash();
      cleanDevDashParam();
      return 'deactivated';
    }

    // ?devdash=true — prompt for password
    if (devdashParam === 'true') {
      cleanDevDashParam();

      // biome-ignore lint/suspicious/noAlert: Intentional prompt for DevDash password entry
      const entered = window.prompt('Enter the DevDash password:');

      if (entered !== null && validatePassword(entered)) {
        enableDevDash();
        return 'activated';
      }

      return 'denied';
    }

    // Any other value — ignore
    cleanDevDashParam();
    return 'none';
  } catch {
    return 'none';
  }
}

/**
 * Main gating function - determines if DevDash should be available
 *
 * Gating rules (checked in order):
 *   1. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   2. ON if localStorage devdash.enabled=true
 *   3. OFF otherwise (opt-in required)
 */
export function isDevDashEnabled(): boolean {
  // 1. Environment variable override
  if (isDisabledByEnvVar()) {
    return false;
  }

  // 2. User must have opted in via localStorage
  if (isDevDashEnabledByUser()) {
    return true;
  }

  // 3. Disabled by default - opt-in required
  return false;
}

/**
 * Get environment badge color for display
 */
export function getEnvBadgeColor(): string {
  const env = getCurrentEnvironment();
  switch (env) {
    case 'local':
      return '#10b981'; // Green
    case 'dev':
      return '#3b82f6'; // Blue
    case 'qa':
      return '#8b5cf6'; // Purple
    case 'prod':
    case 'production':
      return '#ef4444'; // Red
    case 'stage':
      return '#f59e0b'; // Amber
    default:
      return '#6b7280'; // Gray
  }
}
